/**
 * Core stabilized metric scheduler.
 *
 * Runs a `requestAnimationFrame` loop, polling a probe until `FrameState` is stable for
 * `stableFramesRequired` consecutive frames, then reports success or failure via
 * `ReliabilityMetrics` (including timeout, invalid start time, immediate failure, and probe
 * errors). Superseded and abandoned runs use {@link cancelMetric} only—those paths do **not**
 * publish `failureWithDuration` so Reliability/UFO stay quiet for churn and lifecycle.
 *
 * **Superseding:** Each `runStabilizedScheduler` call first calls {@link cancelMetric} on the
 * same {@link MetricRef} to stop the prior rAF loop without a reliability row, then starts
 * the new probe.
 *
 * **Cancellation:** {@link cancelMetric} stops the rAF loop and clears the ref; it never
 * publishes a failure (used for supersede, tab hide, sink teardown, React unmount, etc.).
 *
 * **Ownership / re-entrancy:** {@link cancelAnimationFrame} removes the **next** scheduled
 * `tick` for that token’s `rafId`; it does not unwind a `tick` that is already executing.
 * Each in-flight run therefore carries a {@link InFlightMetricToken.runHandle}; after
 * synchronous cancel or supersede inside `getFrameState`, the resumed `tick` compares
 * `ref.current?.runHandle` and skips publishing and re-scheduling when this run is no longer
 * the owner.
 */
import { type MetricNameValue, ReliabilityMetrics } from '@canvas/frontend-metrics';
import type { CanvasMetadata } from '@canvas/frontend-metrics/reliability-metrics';

import { buildDatabaseSizeMetadata } from './build-database-size-metadata.ts';

/**
 * Legacy `errorMessage` value for same-ref supersede; **no longer emitted** (supersede is
 * silent). Kept for filters on historical Reliability data.
 */
export const STABILIZED_METRIC_SUPERSEDED_REASON = 'superseded-by-new-interaction';

const DEFAULT_MAX_WAIT_MS = 120_000;
const DEFAULT_STABLE_FRAMES_REQUIRED = 2;
const START_TIME_FUTURE_SLACK_MS = 50;
const PROBE_ERROR_MESSAGE_MAX = 500;

/**
 * State returned by a probe function each frame.
 *
 * `done: true` with a consistent non-null `stabilityKey` across
 * {@link SchedulerParams.stableFramesRequired} frames triggers success; see
 * {@link runStabilizedScheduler}.
 */
export interface FrameState {
  done: boolean;
  stabilityKey: string | null;
}

/**
 * Parameters for {@link runStabilizedScheduler}.
 */
export interface SchedulerParams {
  /**
   * Start of the measured interval (`performance.now()`). Clamped to the scheduler's
   * anchor time when slightly in the future; invalid values fail before the rAF loop runs.
   */
  startTime: number;

  /** Reliability metric name used for success and failure reports. */
  metricName: MetricNameValue;

  /**
   * Invoked every animation frame until success, timeout, or probe error. Must be cheap;
   * see {@link FrameState} for how `done` and `stabilityKey` gate completion.
   */
  getFrameState: () => FrameState;

  /** Merged into ReliabilityMetrics payloads for both success and failure. */
  metadata?: CanvasMetadata;

  /**
   * Maximum elapsed ms from the effective `startTime` before reporting
   * `stabilized-metric-timeout`. When omitted, the scheduler uses an internal default
   * (120_000 ms).
   */
  maxWaitMs?: number;

  /**
   * Number of consecutive frames where `getFrameState` returns `done: true` and the same
   * non-null `stabilityKey` before reporting success. When omitted, defaults to 2.
   */
  stableFramesRequired?: number;

  /**
   * When set, skips the rAF loop and records an immediate failure whose `errorMessage`
   * is `reason`.
   */
  failImmediately?: { reason: string };
}

/**
 * Token stored on {@link MetricRef} while a stabilized run is active.
 */
export type InFlightMetricToken = {
  /** `requestAnimationFrame` handle passed to {@link cancelAnimationFrame}. */
  rafId: number;

  /**
   * Opaque object unique to one `runStabilizedScheduler` invocation. Written on
   * `ref.current` together with `rafId` each time a frame is scheduled. After
   * {@link cancelMetric} or a nested scheduler run replaces `ref.current`, this handle no
   * longer matches, so the prior `tick` (if it continues after a synchronous call from inside
   * `getFrameState`) does not call `failureWithDuration` / `successWithDuration` or queue
   * another `requestAnimationFrame` for that superseded run.
   */
  runHandle: object;
};

/**
 * Mutable ref for one concurrent stabilized metric; pass the same ref to every `fireMetric`
 * call for that slot (see `@databases/frontend-metrics/fire-metric`). Holds an
 * {@link InFlightMetricToken} while a probe is in flight, or `null` when idle.
 */
export type MetricRef = { current: InFlightMetricToken | null };

/**
 * Creates a new {@link MetricRef} for tracking an in-flight metric.
 */
export function createMetricRef(): MetricRef {
  return { current: null };
}

/**
 * Cancels the in-flight rAF loop for `ref` by calling {@link cancelAnimationFrame} on the
 * stored `rafId`, then setting `ref.current` to `null`. That clears the active
 * {@link InFlightMetricToken.runHandle} from the ref so any `tick` still finishing after a
 * synchronous call from `getFrameState` treats itself as non-owner. Does not publish to
 * Reliability.
 */
export function cancelMetric(ref: MetricRef): void {
  const token = ref.current;

  if (!token) {
    return;
  }

  cancelAnimationFrame(token.rafId);

  ref.current = null;
}

function resolveEffectiveStartTime(
  rawStart: number,
  anchorTime: number
): { ok: true; effectiveStart: number } | { ok: false; reason: string } {
  if (!Number.isFinite(rawStart)) {
    return { ok: false, reason: 'stabilized-metric-invalid-start-time' };
  }

  if (rawStart > anchorTime + START_TIME_FUTURE_SLACK_MS) {
    return { ok: false, reason: 'stabilized-metric-start-time-in-future' };
  }

  return { ok: true, effectiveStart: rawStart > anchorTime ? anchorTime : rawStart };
}

function truncateForMetadata(message: string): string {
  if (message.length <= PROBE_ERROR_MESSAGE_MAX) {
    return message;
  }

  return `${message.slice(0, PROBE_ERROR_MESSAGE_MAX)}…`;
}

/**
 * Starts the stabilized rAF loop on `ref`.
 *
 * **Supersede:** Replaces any prior in-flight run on the same `ref` by calling
 * {@link cancelMetric} (silent) for the replaced run, then schedules the new probe.
 *
 * **While running:** Writes an {@link InFlightMetricToken} to `ref.current` until the probe
 * stabilizes (success), exceeds `maxWaitMs` (timeout), throws in `getFrameState` (probe error),
 * or {@link cancelMetric} clears the ref.
 *
 * **Ownership:** Each invocation allocates a fresh `runHandle` on the token; `tick` checks
 * `ref.current?.runHandle === runHandle` after `getFrameState` and before timeout/success
 * publishes and before scheduling the next frame, so synchronous supersede/cancel cannot
 * leave a stale closure publishing or re-arming rAF.
 *
 * @param params - Timing, metric name, probe factory result, metadata, limits, and optional
 *   immediate failure; see {@link SchedulerParams}.
 * @param ref - Mutable slot for the in-flight token; must be stable across fires for that metric slot.
 */
export function runStabilizedScheduler(params: SchedulerParams, ref: MetricRef): void {
  cancelMetric(ref);

  const anchorTime = performance.now();
  const {
    startTime: rawStartTime,
    metricName,
    getFrameState,
    metadata,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    stableFramesRequired = DEFAULT_STABLE_FRAMES_REQUIRED,
    failImmediately
  } = params;

  const timing = resolveEffectiveStartTime(rawStartTime, anchorTime);

  const mergePublishMetadata = () => ({
    ...metadata,
    ...buildDatabaseSizeMetadata(metadata)
  });

  if (!timing.ok) {
    const failMeta = { ...mergePublishMetadata(), errorMessage: timing.reason };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      anchorTime,
      anchorTime,
      failMeta
    );

    return;
  }

  const startTime = timing.effectiveStart;

  if (failImmediately) {
    const endTime = performance.now();
    const failMeta = { ...mergePublishMetadata(), errorMessage: failImmediately.reason };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );

    return;
  }

  let stableStreak = 0;
  let lastStabilityKey: string | null = null;

  const runHandle = {};
  const stillOwner = () => ref.current?.runHandle === runHandle;

  const finishSuccess = () => {
    const endTime = performance.now();
    const baseMeta = mergePublishMetadata();

    void ReliabilityMetrics.getInstance().successWithDuration(
      metricName,
      startTime,
      endTime,
      baseMeta
    );
  };

  const finishTimeout = () => {
    const endTime = performance.now();
    const failMeta = {
      ...mergePublishMetadata(),
      errorMessage: 'stabilized-metric-timeout'
    };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );
  };

  const finishProbeError = (error: unknown) => {
    const endTime = performance.now();
    const detail = error instanceof Error ? error.message : String(error);
    const failMeta = {
      ...mergePublishMetadata(),
      errorMessage: `stabilized-metric-probe-error:${truncateForMetadata(detail)}`
    };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );
  };

  const tokenPayload = (rafId: number): InFlightMetricToken => ({
    rafId,
    runHandle
  });

  const tick = () => {
    if (!stillOwner()) {
      return;
    }

    const elapsed = performance.now() - startTime;

    if (elapsed > maxWaitMs) {
      if (!stillOwner()) {
        return;
      }

      ref.current = null;

      finishTimeout();

      return;
    }

    let frameState: FrameState;

    try {
      frameState = getFrameState();
    } catch (error) {
      if (!stillOwner()) {
        return;
      }

      ref.current = null;

      finishProbeError(error);

      return;
    }

    if (!stillOwner()) {
      return;
    }

    const { done, stabilityKey } = frameState;

    if (done && stabilityKey !== null) {
      if (lastStabilityKey === stabilityKey) {
        stableStreak += 1;
      } else {
        stableStreak = 1;
        lastStabilityKey = stabilityKey;
      }

      if (stableStreak >= stableFramesRequired) {
        if (!stillOwner()) {
          return;
        }

        ref.current = null;

        finishSuccess();

        return;
      }
    } else {
      stableStreak = 0;
      lastStabilityKey = null;
    }

    if (!stillOwner()) {
      return;
    }

    ref.current = tokenPayload(requestAnimationFrame(tick));
  };

  ref.current = tokenPayload(requestAnimationFrame(tick));
}
